import React from 'react';
import { Stack, Toggle } from '@fluentui/react';
import { FluentIconsProps } from '@fluentui/react-icons';
import { useAppTheme } from 'common';
import { IStyledComponent } from 'common/Common.types';
import { ActionButton, PillButton } from 'common/components/Buttons/v8';
import { ForwardIcon } from 'common/components/Icons/ForwardIcon';
import { Tag } from 'common/components/Tag';
import { Heading3, Text } from 'common/components/Text';
import TelemetryProvider, { Properties } from 'common/utilities/TelemetryProvider';
import { useLocalizedStrings } from 'i18n';

import { usePanelMobileView } from '../../../Panel/usePanelMobileView';

import { NotificationSettingsConfirmationContext } from './Confirmation/NotificationSettingsConfirmationContext';
import { stackTokens, useNotificationSettingsStyles, useNotificationToggleStyles } from './NotificationSettings.styles';

export interface INotificationSettingsSummaryProps extends IStyledComponent {
  notificationType: string;
  telemetryProps?: Properties;
  onRenderTitle?: () => React.ReactNode | React.ReactNode[] | null;
  title?: string;
  iconComponent?: React.ComponentType<FluentIconsProps>;
  text?: string;
  toggleNotificationLabel: string;
  toggleAriaLabel?: string;
  notificationsEnabled: boolean;
  onToggleNotification: (on: boolean) => void;
  onDrillDown?: () => void;
  drillDownButtonAriaLabel?: string;
  enabledTopicShortNames?: string[];
  turnOffConfirmStrings?: {
    title: string;
    subText: string;
  };
  disabledReason?: string;
  isToggleable?: boolean;
}
export const NotificationSettingsSummary = (props: INotificationSettingsSummaryProps) => {
  const {
    onRenderTitle,
    title,
    iconComponent: IconComponent,
    text,
    toggleNotificationLabel,
    toggleAriaLabel,
    notificationsEnabled,
    onToggleNotification,
    onDrillDown,
    drillDownButtonAriaLabel,
    enabledTopicShortNames,
    className,
    styles,
    notificationType,
    telemetryProps,
    turnOffConfirmStrings,
    disabledReason,
    isToggleable = true,
  } = props;

  const { t } = useLocalizedStrings();
  const toggleStyles = useNotificationToggleStyles();
  const classNames = useNotificationSettingsStyles();
  const { isMobileView } = usePanelMobileView();
  const theme = useAppTheme();

  const { showConfirmationPanel } = React.useContext(NotificationSettingsConfirmationContext);

  const onConfirmTurnOff = React.useCallback(() => {
    TelemetryProvider.Instance.logUserActivity('NotificationSettingToggled', 'ClickActivity', {
      ...(telemetryProps || {}),
      enabled: false,
      notificationType,
    });
    onToggleNotification(false);
  }, [telemetryProps, notificationType, onToggleNotification]);

  const onToggle = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
      if (!checked && turnOffConfirmStrings) {
        showConfirmationPanel(turnOffConfirmStrings.title, turnOffConfirmStrings.subText, onConfirmTurnOff);
      } else {
        TelemetryProvider.Instance.logUserActivity('NotificationSettingToggled', 'ClickActivity', {
          ...(telemetryProps || {}),
          enabled: checked,
          notificationType,
        });
        onToggleNotification(checked ?? false);
      }
    },
    [turnOffConfirmStrings, telemetryProps, notificationType, showConfirmationPanel, onConfirmTurnOff, onToggleNotification]
  );

  const shouldRenderTags = notificationsEnabled && !!enabledTopicShortNames?.length;

  const renderMobileView = React.useCallback(() => {
    const valueText =
      notificationsEnabled && !disabledReason
        ? t('OfficeShellPanel.NotificationSettings.Sections.On')
        : t('OfficeShellPanel.NotificationSettings.Sections.Off');

    const contents = (
      <Stack className={classNames.mobileViewSummaryButtonStack}>
        <Stack horizontal verticalAlign="center" tokens={stackTokens.gapLG}>
          {IconComponent && (
            <Stack disableShrink>
              <IconComponent primaryFill={theme.appColors?.lightHeadlineText} />
            </Stack>
          )}
          <Stack.Item grow>
            {onRenderTitle ? (
              <Stack.Item disableShrink>{onRenderTitle()}</Stack.Item>
            ) : (
              <Heading3 variant="Title2Strong" block style={{ textAlign: 'left' }}>
                {title || ''}
              </Heading3>
            )}
          </Stack.Item>

          {onDrillDown ? (
            <Stack disableShrink horizontal verticalAlign="center" tokens={stackTokens.gapXS}>
              <Stack.Item>
                <Text variant="Body1">{valueText}</Text>
              </Stack.Item>
              <Stack>
                <ForwardIcon primaryFill={theme.appColors?.lightHeadlineText} />
              </Stack>
            </Stack>
          ) : (
            <Stack disableShrink horizontal verticalAlign="center" tokens={stackTokens.gapXS}>
              <Toggle
                ariaLabel={toggleAriaLabel ?? valueText}
                checked={disabledReason ? false : notificationsEnabled}
                onChange={onToggle}
                styles={toggleStyles}
                disabled={!!disabledReason || !isToggleable}
              />
              <Text variant="Body1">{valueText}</Text>
            </Stack>
          )}
        </Stack>
        {disabledReason && <Text className={classNames.disabledLabel}>{disabledReason}</Text>}
      </Stack>
    );

    return (
      <Stack tokens={stackTokens.gapMD} className={className} styles={styles}>
        {onDrillDown ? (
          <PillButton
            className={classNames.mobileViewSummaryButton}
            onClick={onDrillDown}
            telemetryEventName="NotificationSettingDrillDown"
            telemetryProps={{
              ...(telemetryProps || {}),
              notificationType,
            }}
            ariaLabel={drillDownButtonAriaLabel}
          >
            {contents}
          </PillButton>
        ) : (
          <Stack verticalAlign="center" className={classNames.mobileViewSummaryButton}>
            {contents}
          </Stack>
        )}
        {(text || shouldRenderTags) && (
          <Stack tokens={stackTokens.gapXS} className={classNames.summary}>
            {text && (
              <Stack.Item>
                <Text variant="Caption2" block>
                  {text}
                </Text>
              </Stack.Item>
            )}
            {shouldRenderTags && (
              <Stack horizontal wrap tokens={stackTokens.gapXXS}>
                {enabledTopicShortNames.map((topicShortName) => (
                  <Tag key={topicShortName} state="Grey" text={topicShortName} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    );
  }, [
    notificationsEnabled,
    disabledReason,
    t,
    classNames.mobileViewSummaryButtonStack,
    classNames.disabledLabel,
    classNames.mobileViewSummaryButton,
    classNames.summary,
    IconComponent,
    theme.appColors?.lightHeadlineText,
    onRenderTitle,
    title,
    onDrillDown,
    toggleAriaLabel,
    onToggle,
    toggleStyles,
    isToggleable,
    className,
    styles,
    telemetryProps,
    notificationType,
    drillDownButtonAriaLabel,
    text,
    shouldRenderTags,
    enabledTopicShortNames,
  ]);

  const renderLargeScreenView = React.useCallback(() => {
    return (
      <Stack className={className} styles={styles} tokens={stackTokens.gapMD}>
        {(title || IconComponent || onRenderTitle) && (
          <Stack horizontal verticalAlign="center" tokens={stackTokens.gapXS}>
            {IconComponent && (
              <Stack verticalAlign="center">
                <IconComponent primaryFill={theme.appColors?.lightHeadlineText} />
              </Stack>
            )}
            {onRenderTitle && <Stack.Item disableShrink>{onRenderTitle()}</Stack.Item>}
            {!onRenderTitle && title && (
              <Heading3 variant="Title2Strong" block>
                {title}
              </Heading3>
            )}
          </Stack>
        )}

        {(text || shouldRenderTags) && (
          <Stack tokens={stackTokens.gapXS} className={classNames.summary}>
            {text && (
              <Stack.Item>
                <Text variant="Body1" block>
                  {text}
                </Text>
              </Stack.Item>
            )}
            {shouldRenderTags && (
              <Stack horizontal wrap tokens={stackTokens.gapXXS}>
                {enabledTopicShortNames.map((topicShortName) => (
                  <Tag key={topicShortName} state="Grey" text={topicShortName} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
        <Stack horizontal verticalAlign="center" tokens={stackTokens.gapSM}>
          <Toggle
            ariaLabel={toggleAriaLabel ?? toggleNotificationLabel}
            checked={disabledReason ? false : notificationsEnabled}
            onChange={onToggle}
            styles={toggleStyles}
            disabled={!!disabledReason || !isToggleable}
          />
          {toggleNotificationLabel && <Text variant="Body1">{toggleNotificationLabel}</Text>}
        </Stack>
        {disabledReason && <Text className={classNames.disabledLabel}>{disabledReason}</Text>}
        {onDrillDown && (
          <Stack.Item disableShrink>
            <ActionButton
              onClick={onDrillDown}
              telemetryEventName="NotificationSettingDrillDown"
              telemetryProps={{
                ...(telemetryProps || {}),
                notificationType,
              }}
              ariaLabel={drillDownButtonAriaLabel}
            >
              <Text variant="Body1Strong" color="blue20ToWhite" className={classNames.drillDownText}>
                {t('OfficeShellPanel.NotificationSettings.Sections.EditPreferences')}
              </Text>
              <ForwardIcon size={12} />
            </ActionButton>
          </Stack.Item>
        )}
      </Stack>
    );
  }, [
    className,
    styles,
    title,
    IconComponent,
    onRenderTitle,
    theme.appColors?.lightHeadlineText,
    text,
    shouldRenderTags,
    classNames.summary,
    classNames.disabledLabel,
    classNames.drillDownText,
    enabledTopicShortNames,
    toggleNotificationLabel,
    toggleAriaLabel,
    disabledReason,
    notificationsEnabled,
    onToggle,
    toggleStyles,
    isToggleable,
    onDrillDown,
    telemetryProps,
    notificationType,
    drillDownButtonAriaLabel,
    t,
  ]);

  return isMobileView ? renderMobileView() : renderLargeScreenView();
};
